import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./ArticleScreen.css";
import { ARTICLES, IMAGES_BY_SLUG, CATEGORIE_COLORS } from "../../data/articles";
import { subscribeNewsletter } from "../../services/newsletterService";

const ARTICLES_BY_KEY = Object.fromEntries(ARTICLES.map((a) => [a.key, a]));
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseFrontmatter(raw) {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { meta: {}, content: raw };

  const meta = {};
  match[1].split(/\r?\n/).forEach((line) => {
    const idx = line.indexOf(":");
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim().replace(/^["']|["']$/g, "");
    meta[key] = value;
  });

  return { meta, content: match[2] };
}

function CategorieBadge({ categorie }) {
  const style = CATEGORIE_COLORS[categorie] || CATEGORIE_COLORS["Actualité"];
  return (
    <span className="article-badge" style={{ background: style.bg, color: style.color }}>
      {categorie}
    </span>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) { setEmailError("Veuillez saisir une adresse e-mail valide."); return; }
    setEmailError("");
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      setSubmitted(true);
    } catch (err) {
      setEmailError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return <p className="article-newsletter-thanks">Merci ! Vous êtes bien inscrit à notre newsletter.</p>;
  }

  return (
    <form className="article-newsletter-form" onSubmit={handleSubmit}>
      <input
        type="email"
        className="article-newsletter-input"
        placeholder="Votre adresse e-mail"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
      />
      {emailError && <p className="article-newsletter-error">{emailError}</p>}
      <button type="submit" className="article-newsletter-btn" disabled={loading}>
        {loading ? "Inscription…" : "S'inscrire"}
      </button>
    </form>
  );
}

function applyServiceCards(container) {
  const h2s = Array.from(container.querySelectorAll('h2'));
  const spstSection = h2s.find((h2) => {
    let el = h2.nextElementSibling;
    while (el && el.tagName !== 'H2') {
      if (el.tagName === 'H3') return true;
      el = el.nextElementSibling;
    }
    return false;
  });
  if (!spstSection) return;

  const groups = [];
  let currentGroup = null;
  let el = spstSection.nextElementSibling;

  while (el && el.tagName !== 'H2') {
    const next = el.nextElementSibling;
    if (el.tagName === 'H3') {
      currentGroup = { name: el.textContent.trim(), h3El: el, children: [] };
      groups.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.children.push(el);
    }
    el = next;
  }

  if (groups.length === 0) return;

  const grid = document.createElement('div');
  grid.className = 'spsti-cards-grid';

  groups.forEach(({ name, h3El, children }) => {
    const card = document.createElement('div');
    card.className = 'spsti-card';

    const title = document.createElement('div');
    title.className = 'spsti-card-title';
    title.textContent = name;
    card.appendChild(title);

    const body = document.createElement('div');
    body.className = 'spsti-card-body';
    children.forEach((child) => body.appendChild(child));
    card.appendChild(body);

    grid.appendChild(card);
    h3El.remove();
  });

  spstSection.after(grid);

  const resourcesH2 = document.createElement('h2');
  resourcesH2.textContent = 'Autres ressources de recherche';

  const resourcesP = document.createElement('p');
  resourcesP.innerHTML =
    'Les informations présentées s\'appuient sur l\'<a href="https://www.presanse.fr/annuaire-des-spsti/" target="_blank" rel="noopener noreferrer">annuaire Présanse</a> ' +
    'et l\'<a href="https://www.sante.fr/recherche/trouver/Services%20de%20Pr%C3%A9vention%20et%20de%20Sant%C3%A9%20au%20Travail%20%28SPST%29" target="_blank" rel="noopener noreferrer">annuaire Santé.fr</a>. ' +
    'Seuls les services eux-mêmes sont en mesure de confirmer les possibilités d\'adhésion selon votre activité et votre zone géographique : n\'hésitez pas à les contacter directement. ' +
    'Ces données sont susceptibles d\'avoir évolué depuis la dernière mise à jour de cette page.';

  grid.after(resourcesP);
  grid.after(resourcesH2);
}

function applyCommunitiesShowMore(container) {
  const VISIBLE_COUNT = 15;
  const paras = container.querySelectorAll('p');
  paras.forEach((p) => {
    const strong = p.querySelector('strong');
    if (!strong || !strong.textContent.includes('Communes')) return;

    // Collect text nodes after <strong>
    let textAfter = '';
    p.childNodes.forEach((node) => {
      if (node === strong) return;
      if (node.nodeType === Node.TEXT_NODE || node.nodeName !== 'STRONG') {
        textAfter += node.textContent || node.outerHTML || '';
      }
    });

    const raw = textAfter.trim();
    // Remove trailing period to split cleanly
    const hasPeriod = raw.endsWith('.');
    const withoutPeriod = hasPeriod ? raw.slice(0, -1) : raw;
    const cities = withoutPeriod.split(',').map((c) => c.trim()).filter(Boolean);

    if (cities.length <= VISIBLE_COUNT) return;

    const visible = cities.slice(0, VISIBLE_COUNT);
    const hidden = cities.slice(VISIBLE_COUNT);
    const suffix = hasPeriod ? '.' : '';

    const strongClone = document.createElement('strong');
    strongClone.innerHTML = strong.innerHTML;

    const overflow = document.createElement('span');
    overflow.className = 'communes-overflow';
    overflow.hidden = true;
    overflow.textContent = ', ' + hidden.join(', ') + suffix;

    const btn = document.createElement('button');
    btn.className = 'communes-more-btn';
    btn.textContent = `… et ${hidden.length} autres communes`;
    btn.addEventListener('click', () => {
      overflow.removeAttribute('hidden');
      btn.style.display = 'none';
    });

    p.innerHTML = '';
    p.appendChild(strongClone);
    p.appendChild(document.createTextNode(' ' + visible.join(', ')));
    p.appendChild(overflow);
    p.appendChild(document.createTextNode(' '));
    p.appendChild(btn);
  });
}

function ArticleScreen({ match }) {
  const { slug } = match.params;
  const [meta, setMeta] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [error, setError] = useState(false);
  const contentRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`/content/blog/${slug}.md`);
        if (!res.ok) throw new Error("Not found");
        const raw = await res.text();
        const { meta: parsedMeta, content } = parseFrontmatter(raw);

        // marked is loaded via CDN or installed — use dynamic import fallback
        let html = content;
        try {
          const { marked } = await import("marked");
          html = marked.parse(content);
        } catch {
          // fallback: basic paragraph wrapping
          html = content
            .split(/\r?\n\r?\n/)
            .map((p) => `<p>${p.replace(/\r?\n/g, "<br/>")}</p>`)
            .join("");
        }

        if (!cancelled) {
          setMeta(parsedMeta);
          setHtmlContent(html);
          document.title = `${parsedMeta.titre} | RAVAIL`;
          const descEl = document.querySelector('meta[name="description"]');
          if (descEl && parsedMeta.description) descEl.setAttribute("content", parsedMeta.description);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    }

    load();
    return () => {
      cancelled = true;
      document.title = "RAVAIL - Travail Santé";
      const descEl = document.querySelector('meta[name="description"]');
      if (descEl) descEl.setAttribute("content", "");
    };
  }, [slug]);

  useEffect(() => {
    if (!contentRef.current || !htmlContent) return;
    applyCommunitiesShowMore(contentRef.current);
    if (meta?.categorie === 'Annuaire SPSTI') {
      applyServiceCards(contentRef.current);
    }
  }, [htmlContent, meta]);

  if (error) {
    return (
      <div className="article-page">
        <div className="article-inner">
          <div className="article-not-found">
            <p>Article introuvable.</p>
            <Link to="/blog" className="article-back-link">← Retour au blog</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="article-page">
        <div className="article-inner article-loading">
          <div className="article-skeleton article-skeleton--title" />
          <div className="article-skeleton article-skeleton--subtitle" />
          <div className="article-skeleton article-skeleton--img" />
          <div className="article-skeleton article-skeleton--body" />
          <div className="article-skeleton article-skeleton--body" />
          <div className="article-skeleton article-skeleton--body article-skeleton--short" />
        </div>
      </div>
    );
  }

  const coverImg = IMAGES_BY_SLUG[slug];

  return (
    <div className="article-page">

      {/* ── HERO ── */}
      <section className="article-hero">
        <div className="article-inner">
          <div className={coverImg ? "article-hero-layout article-hero-layout--with-img" : "article-hero-layout"}>
            <div className="article-hero-text">
              <Link to="/blog" className="article-back-link">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 13L5 8L10 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Retour au blog
              </Link>

              <div className="article-hero-meta">
                <CategorieBadge categorie={meta.categorie} />
                <span className="article-hero-date">Publié le {meta.date}</span>
                {meta.updated && (
                  <span className="article-hero-date">· Mis à jour le {meta.updated}</span>
                )}
              </div>

              <h1 className="article-hero-title">{meta.titre}</h1>

              {meta.description && (
                <p className="article-hero-desc">{meta.description}</p>
              )}
            </div>

            {coverImg && (
              <div className="article-hero-img-col">
                <img src={coverImg} alt={meta.titre} className="article-hero-img" />
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="wave-article-hero" aria-hidden="true">
        <svg viewBox="0 0 1280 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 H1280 V60 C1000 0 300 120 0 40 Z" fill="#ffffff"/>
        </svg>
      </div>

      {/* ── CONTENU ── */}
      <section className={`article-section${coverImg ? " article-section--with-img" : ""}`}>
        <div className="article-inner">
          <div className="article-layout">

            {/* Colonne principale */}
            <article className="article-body">
              <div
                ref={contentRef}
                className="article-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </article>

            {/* Sidebar */}
            <aside className="article-sidebar">
              <div className="article-sidebar-card">
                <p className="article-sidebar-label">Catégorie</p>
                <CategorieBadge categorie={meta.categorie} />
              </div>
              <div className="article-sidebar-card">
                <p className="article-sidebar-label">Date de publication</p>
                <p className="article-sidebar-value">{meta.date}</p>
              </div>
              {meta.updated && (
                <div className="article-sidebar-card">
                  <p className="article-sidebar-label">Dernière mise à jour</p>
                  <p className="article-sidebar-value">{meta.updated}</p>
                </div>
              )}
              <div className="article-sidebar-cta">
                <p className="article-sidebar-cta-text">Inscrivez-vous à notre newsletter</p>
                <NewsletterForm />
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* ── ARTICLES LIÉS ── */}
      {(() => {
        const current = ARTICLES_BY_KEY[slug];
        const relatedArticles = (current?.related || [])
          .map((key) => ARTICLES_BY_KEY[key])
          .filter(Boolean);
        if (!relatedArticles.length) return null;
        return (
          <section className="article-related">
            <div className="article-inner">
              <h2 className="article-related-title">À lire également</h2>
              <div className="article-related-grid">
                {relatedArticles.map((a) => (
                  <Link key={a.key} to={`/blog/${a.key}`} className="article-related-card">
                    {IMAGES_BY_SLUG[a.key] && (
                      <div className="article-related-img-wrap">
                        <img src={IMAGES_BY_SLUG[a.key]} alt={a.titre} className="article-related-img" />
                      </div>
                    )}
                    <div className="article-related-body">
                      <CategorieBadge categorie={a.categorie} />
                      <p className="article-related-card-title">{a.titre}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}
    </div>
  );
}

export default ArticleScreen;
